import i18n from '@/content';

import { getErrorResponse } from '@/components/utils/APIErrorsHandlers';

const NAME_SPACE = 'general.loadErrorReport';
const LABELS_NAME_SPACE = `${NAME_SPACE}.labels`;

/** Network error message normalization, mirrors getDebugInfo.ts. */
const NETWORK_ERROR_MESSAGE = 'No response data available. Possible CORS issue';

/**
 * Diagnostic data collected at a file-load failure. All fields are SpecterX-side
 * identifiers (SharePoint files are backend-managed), matching what the recipient
 * file-access error surface already exposes via baseDebugInfo.
 */
export interface ErrorDiagnostics {
    referenceId: string;
    time: string;
    /** HTTP status code from the caught error; undefined for network errors. */
    statusCode?: number;
    /** Normalized error message (network errors collapse to the CORS hint). */
    message: string;
    /** SpecterX file IDs (fid); include all when multiple. */
    fileIds: string[];
    /** Present in recipient-link flows; omitted otherwise. */
    linkId?: string;
    /** Logged-in user email. */
    user: string;
    /** Logged-in user id (Cognito sub / IdP sub). */
    userId: string;
    /** Rendered only when present (organizationsStore / localStorage). */
    organizationId?: string;
    /** Deploy stage (import.meta.env.VITE_STAGE). */
    environment: string;
    /** Current URL. */
    page: string;
    /** navigator.userAgent. */
    browser: string;
}

/** Stable top-of-list ordering, mirrors DebugInfo's TOP_ORDER_KEYS. */
const TOP_ORDER_KEYS = ['status', 'message'];

const label = (key: keyof typeof LABEL_KEYS): string => i18n.t(`${LABELS_NAME_SPACE}.${LABEL_KEYS[key]}`);

const LABEL_KEYS = {
    statusCode: 'statusCode',
    message: 'message',
    fileIds: 'fileIds',
    linkId: 'linkId',
    user: 'user',
    userId: 'userId',
    organizationId: 'organizationId',
    environment: 'environment',
    page: 'page',
    browser: 'browser',
    referenceId: 'referenceId',
    time: 'time',
    userNote: 'userNote',
} as const;

/**
 * Ordered key/value pairs (labels via i18n), with status code + message pinned first.
 * Organization ID is omitted when absent.
 */
export const toOrderedEntries = (
    d: ErrorDiagnostics,
): ReadonlyArray<readonly [string, string]> => {
    const entries: Array<readonly [string, string]> = [
        [label('referenceId'), d.referenceId],
        [label('time'), d.time],
    ];
    if (d.statusCode !== undefined) {
        entries.push([label('statusCode'), String(d.statusCode)]);
    }
    entries.push(
        [label('message'), d.message],
        [label('fileIds'), d.fileIds.join(', ')],
    );
    if (d.linkId) {
        entries.push([label('linkId'), d.linkId]);
    }
    entries.push(
        [label('user'), d.user],
        [label('userId'), d.userId],
    );
    if (d.organizationId) {
        entries.push([label('organizationId'), d.organizationId]);
    }
    entries.push(
        [label('environment'), d.environment],
        [label('page'), d.page],
        [label('browser'), d.browser],
    );
    return entries;
};

/**
 * Shared report text used by BOTH "Copy all" and the email body:
 * 1-line intro, blank line, optional user note, then the diagnostics.
 */
export const buildReportText = (d: ErrorDiagnostics, note?: string): string => {
    const intro = i18n.t(`${NAME_SPACE}.email.intro`);
    const lines: string[] = [intro, ''];
    const trimmedNote = note?.trim();
    if (trimmedNote) {
        lines.push(`${label('userNote')}: ${trimmedNote}`, '');
    }
    toOrderedEntries(d).forEach(([key, value]) => lines.push(`${key}: ${value}`));
    return lines.join('\n');
};

/**
 * Normalize a comma- or semicolon-separated recipient string into a clean
 * comma-joined list (no spaces, no empties), as required by the mailto "to" field
 * (RFC 6068). Spaces between addresses break many mail clients.
 */
const normalizeRecipients = (recipients: string): string => recipients
    .split(/[,;]/)
    .map((addr) => addr.trim())
    .filter(Boolean)
    .join(',');

/** mailto href for "Open in email". */
export const buildMailtoHref = (d: ErrorDiagnostics, helpdeskRecipients: string, note?: string): string => {
    const subject = i18n.t(`${NAME_SPACE}.email.subject`, { referenceId: d.referenceId });
    const body = buildReportText(d, note);
    // The "to" field is left unencoded (commas separate recipients per RFC 6068);
    // only subject/body are percent-encoded.
    return `mailto:${normalizeRecipients(helpdeskRecipients)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

/** Normalize a caught error into statusCode + message, mirroring getDebugInfo. */
export const normalizeError = (error: unknown): Pick<ErrorDiagnostics, 'statusCode' | 'message'> => {
    const { statusCode, message } = getErrorResponse(error);
    const resolvedMessage = message
        || (error as Error)?.message
        || (String(error).toLowerCase() === 'network error' ? NETWORK_ERROR_MESSAGE : String(error));
    const isNetwork = !statusCode && (!message && (error as Error)?.message?.toLowerCase() === 'network error');
    return {
        statusCode: statusCode || undefined,
        message: isNetwork ? NETWORK_ERROR_MESSAGE : resolvedMessage,
    };
};

export { NETWORK_ERROR_MESSAGE };
